/**
 * Feature Flag System
 *
 * Provides type-safe feature flags with environment-based values.
 * Uses Vite's import.meta.env.MODE to determine the current environment.
 *
 * Usage:
 *   const isFeatureFlagEnabled = useFeatureFlag();
 *   if (isFeatureFlagEnabled('CORE_5526')) {
 *     // feature code
 *   }
 */

import { Stage, useStage } from '@/hooks/useStage';

/**
 * Feature flag definition interface
 */
export interface FeatureFlagDefinition {
  dev?: boolean;
  stage?: boolean;
  prod: boolean;
  defaultValue: boolean;
}

/**
 * Type-safe feature flag definitions
 * Add new flags here with their environment-specific values
 */
const FEATURE_FLAGS: Record<string, FeatureFlagDefinition> = {
  CORE_7492_ARCHIVIERUNG_ENTKOPPELN: {
    dev: true,
    stage: true,
    prod: true,
    defaultValue: true,
  },
  CORE_7497_ANMERKUNGEN: {
    dev: true,
    stage: true,
    prod: false,
    defaultValue: false,
  },
} as const;

/**
 * Union type of all feature flag names for type safety
 */
export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

/**
 * Gets the value for a feature flag based on the current stage
 */
const getFlagValue = (flagName: string, stage: Stage): boolean => {
  const flag = FEATURE_FLAGS[flagName];
  if (!flag) {
    // eslint-disable-next-line no-console
    console.warn(`Feature flag "${flagName}" not found. Returning false.`);
    return false;
  }

  // Check environment-specific value first
  const stageValue = flag[stage];
  if (stageValue !== undefined) {
    return stageValue;
  }

  // Fall back to defaultValue
  return flag.defaultValue;
};

/**
 * React hook for feature flags
 * Returns a function that checks if a feature flag is enabled
 *
 * @returns Function that takes a flag name and returns boolean
 */
export const useFeatureFlag = () => {
  const stage = useStage();

  return (flagName: FeatureFlagName): boolean => {
    return getFlagValue(flagName, stage);
  };
};
